import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import emailjs from '@emailjs/browser';
import type { ReactElement, Ref, RefObject, ReactNode } from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import styles from './EmailForm.module.scss';
import { setupTransitionInTimeline } from './EmailForm.transitions';
import type { EmailFormProps, EmailFormRef } from './EmailForm.data';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import InputField from '../../atoms/input-field/InputField';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { useGlobalStore } from '@/stores/global';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Icon from '../../atoms/icon/Icon';
import CountryDropdown from '../../molecules/country-dropdown/CountryDropdown';
import RegionDropdown from '../../molecules/region-dropdown/RegionDropdown';
import type { BooleanSchema, StringSchema } from 'yup';
import type { CtaRefType } from 'util/cta/cta.data';
import { useThemeColorProviderContext } from '../../unlisted/ThemeColorProvider';
import type { ThemeColorProviderContextType } from '../../unlisted/ThemeColorProvider';
import { yupResolver } from '@hookform/resolvers/yup';

const sendEmail = (formRef: RefObject<HTMLFormElement | null>) =>
  emailjs
    .sendForm(
      process.env.EMAILJS_SERVICE_ID!,
      'template_4xu2dsd',
      formRef.current || '',
      process.env.EMAILJS_KEY,
    )
    .then(
      (response) => {
        console.log('SUCCESS!', response.status, response.text);
      },
      (error) => {
        console.log('FAILED...', error);
      },
    );

export enum FormState {
  Default,
  SubmitSuccess,
  SubmitError,
}

export default function EmailForm({
  blok,
  parentHandleSubmit,
  ref,
  ...props
}: EmailFormProps & { ref?: Ref<EmailFormRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = localRef as RefObject<HTMLDivElement | null>;
  const globalStore = useGlobalStore();
  const isSubmitting = useRef(false);
  const [formLayoutState, setFormLayoutState] = useState(FormState.Default);
  const buttonRef = useRef<CtaRefType>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { isDarkTheme } = useThemeColorProviderContext() as ThemeColorProviderContextType;

  const validationRules: Record<string, StringSchema | BooleanSchema> = {};

  blok.input_fields?.forEach((field) => {
    let yupString = Yup.string();
    if (field.required) {
      yupString = yupString.required(`${field.label} is required`);
    }
    if (field.type === 'email') {
      yupString = yupString.email('Email is invalid');
    }
    validationRules[field.id] = yupString;
  });

  blok.checkboxes?.forEach((checkbox) => {
    if (checkbox.required) {
      validationRules[checkbox.id] = Yup.bool().oneOf([true], checkbox.error_message);
    }
  });

  const validationSchema = Yup.object().shape(validationRules);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, formState } = useForm(formOptions);
  const { errors } = formState;

  useUpdateEffect(() => {
    ScrollTrigger.refresh();
    if (parentHandleSubmit) {
      scrollContext.scrollTo(0, 0, 600);
    } else {
      const rect = elementRef.current?.getBoundingClientRect();
      const currentY = scrollContext.scrollStatusRef.current?.offset.y || 0;
      scrollContext.scrollTo(0, (rect?.top || 0) + currentY - 200, 600);
    }
  }, [formLayoutState]);

  const onValidationPassed = useCallback(async () => {
    if (!isSubmitting.current) {
      isSubmitting.current = true;

      try {
        await sendEmail(formRef);
        setFormLayoutState(FormState.SubmitSuccess);
        parentHandleSubmit?.(FormState.SubmitSuccess);
      } catch (error) {
        setFormLayoutState(FormState.SubmitError);
        parentHandleSubmit?.(FormState.SubmitError);
      } finally {
        isSubmitting.current = false;
      }
    }
  }, [parentHandleSubmit]);

  useImperativeHandle(
    ref,
    () => ({
      formLayoutState,
    }),
    [formLayoutState],
  );

  const isDark = (isDarkTheme && !blok.use_accent_color) || (!isDarkTheme && blok.use_accent_color);
  const isLight =
    (!isDarkTheme && !blok.use_accent_color) || (isDarkTheme && blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.emailForm, props.className, {
        [styles.darkTheme]: isDark,
        [styles.lightTheme]: isLight,
      })}
      {...sbEditable(blok)}
      data-scroll-to="download-pdf"
    >
      <div className={classNames(styles.container, 'grid-gutter max-content-width')}>
        <form
          onSubmit={handleSubmit(onValidationPassed)}
          className={classNames(styles.form)}
          ref={formRef}
        >
          <div className={classNames(styles.header)}>
            {formLayoutState === FormState.Default && (
              <>
                <Heading
                  transitionInType="splitWordsSlideInUp"
                  className={classNames(styles.title)}
                  as="h3"
                  size="heading-03"
                  disableTriggerInOnScroll={false}
                >
                  {blok.title}
                </Heading>
                {render(blok.description, {
                  nodeResolvers: {
                    [NODE_PARAGRAPH]: (children: ReactNode) => (
                      <Text
                        transitionInType="splitWordsSlideInUp"
                        className={classNames(styles.description)}
                        as="p"
                        size="body-large"
                        disableTriggerInOnScroll={false}
                      >
                        {children}
                      </Text>
                    ),
                  },
                })}
              </>
            )}
            <div className={styles.center}>
              {(formLayoutState === FormState.SubmitSuccess ||
                formLayoutState === FormState.SubmitError) && (
                <>
                  <Heading
                    transitionInType="splitWordsSlideInUp"
                    className={classNames(styles.title)}
                    as="h3"
                    size="heading-03"
                    disableTriggerInOnScroll={false}
                  >
                    {formLayoutState === FormState.SubmitSuccess && blok.success_title}
                    {formLayoutState === FormState.SubmitError && (blok.error_title || 'Apologies')}
                  </Heading>

                  {render(
                    (formLayoutState === FormState.SubmitSuccess && blok.success_description) ||
                      (formLayoutState === FormState.SubmitError &&
                        (blok.error_description ||
                          'Something went wrong, please try again later.')),
                    {
                      nodeResolvers: {
                        [NODE_PARAGRAPH]: (children: ReactNode) => (
                          <Text
                            transitionInType="splitWordsSlideInUp"
                            className={classNames(styles.description)}
                            as="p"
                            size="body-standard"
                            disableTriggerInOnScroll={false}
                          >
                            {children}
                          </Text>
                        ),
                      },
                    },
                  )}
                </>
              )}
            </div>
          </div>

          <div className={styles.center}>
            {formLayoutState === FormState.Default && (
              <>
                <fieldset className={classNames(styles.fieldset)}>
                  <input
                    name="url"
                    defaultValue={typeof window !== 'undefined' ? window.location.href : undefined}
                    hidden
                  />
                  <input name="email_to" defaultValue={blok.email_to} hidden />
                  {blok.input_fields?.map((field) => {
                    let Autocomplete;
                    if (field.type === 'country') Autocomplete = CountryDropdown;
                    if (field.type === 'region') Autocomplete = RegionDropdown;
                    return (
                      <InputField
                        key={field.id}
                        register={register}
                        error={errors[field.id]}
                        className={classNames(styles.inputField)}
                        type={
                          field.type === 'country' || field.type === 'region'
                            ? 'text'
                            : field.type || 'text'
                        }
                        name={field.id}
                        label={field.label}
                        hidden={field.hidden}
                        placeholder={field.placeholder}
                        required={field.required}
                        Autocomplete={Autocomplete}
                      />
                    );
                  })}

                  {blok.checkboxes?.map((checkbox) => (
                    <div
                      className={classNames(styles.checkboxContainer, styles.inputContainer)}
                      key={checkbox.id}
                    >
                      <input type="checkbox" id={checkbox.id} {...register(checkbox.id)} />
                      {render(checkbox.label, {
                        nodeResolvers: {
                          [NODE_PARAGRAPH]: (children: ReactNode) => (
                            <Text
                              as="label"
                              size="body-standard"
                              transitionInType="none"
                              htmlFor={checkbox.id}
                            >
                              {children} {checkbox.required ? '*' : ''}
                            </Text>
                          ),
                        },
                      })}
                      {errors?.[checkbox.id]?.message && (
                        <div className={classNames(styles.errorContainer)}>
                          <Icon className={classNames(styles.icon)} name="error" />
                          <Text
                            as="span"
                            size="foot-note"
                            className={classNames(styles.errorMessage)}
                            transitionInType="none"
                          >
                            <>{errors[checkbox.id]?.message}</>
                          </Text>
                        </div>
                      )}
                    </div>
                  ))}
                </fieldset>

                <div className={classNames(styles.submitHolder)}>
                  <PrimaryButton
                    ref={buttonRef}
                    className={classNames(styles.button)}
                    label={blok.button_label || globalStore.globalSettings.form_submit}
                    type="submit"
                    disableTriggerInOnScroll={false}
                  />
                </div>

                {render(blok.disclaimer, {
                  nodeResolvers: {
                    [NODE_PARAGRAPH]: (children: ReactNode) => (
                      <Text
                        transitionInType="none"
                        className={classNames(styles.disclaimer)}
                        as="p"
                        size="foot-note"
                        disableTriggerInOnScroll={false}
                      >
                        {children}
                      </Text>
                    ),
                  },
                })}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
